import React, { useState, useRef, useEffect } from "react";
import { useStore, StoreTypes } from "context";
import DragModal from "./DragModal";
import styles from "./index.module.scss";
import { QRCodeSVG } from "qrcode.react";
import uuid from "util/uuid";
import * as types from "constants/actionTypes";
import copyIcon from "components/PaiQuiz/copyIcon.png";
import noContent from "components/PaiQuiz/noContent.png";
import BaseSnackbar from "components/BaseSnackbar";
import { LottiePlayer2 } from "../LottieLoading/lottiePlayer";
import { StepStatus } from "constants/aiSpeak";
import classNames from "classnames";

const naniSpeakUrl = process.env.REACT_APP_NANISPEAK_URL;

const AiSpeakContent = ({ stepStatus, isLoading, paperData, qrCodeValue }) => {
  const { paperId, displayName, sentences } = paperData;
  const naniSpeakLink = useRef(null);
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    vertical: "bottom",
    horizontal: "left",
  });
  const { vertical, horizontal, open } = snackbarState;
  const handleSnackbarClose = () => {
    setSnackbarState({ ...snackbarState, open: false });
  };

  const copyLink = (newState) => {
    navigator.clipboard.writeText(naniSpeakLink.current.textContent);
    setSnackbarState({ open: true, ...newState });
  };

  switch (stepStatus) {
    case StepStatus.Preparing: {
      return (
        <div className={styles.emptyContent}>
          <img src={noContent} alt="題目準備中，敬請期待 ～" />
          題目準備中，敬請期待 ～
        </div>
      );
    }
    case StepStatus.Ready: {
      return <div className={styles.readyContent}>
        <div className={styles.qrHeading}>{displayName}</div>
        <div className={styles.qrHeading}>題數：{ sentences.length } 題</div>
      </div>;
    }
    case StepStatus.Testing: {
      const quizSrc = `${naniSpeakUrl}/quiz/${paperId}?code=${qrCodeValue}`;

      return (
        <>
          {isLoading && (
            <div className={styles.loadingContent}>
              <LottiePlayer2 />
              <p>等待中...</p>
            </div>
          )}
          {!isLoading && (
            <>
              <div className={styles.immediateContent}>
                <div className={styles.qrCode}>
                  <QRCodeSVG value={quizSrc} />
                </div>

                <div className={styles.qrTextBlock}>
                  <div className={styles.qrTitle}>AI 練口說</div>
                  <div className={styles.qrHeading}>{displayName}</div>
                  <div className={styles.qrLinkArea}>
                    <div className={styles.linkText}>
                      <span ref={naniSpeakLink}>{quizSrc}</span>
                    </div>
                    <button
                      className={`${styles.linkButton} ${styles.linkButtonDesktop}`}
                      type="button"
                      onClick={() =>
                        copyLink({ vertical: "bottom", horizontal: "left" })
                      }
                    >
                      <img src={copyIcon} alt="複製" />
                      複製連結
                    </button>
                    <button
                      className={`${styles.linkButton} ${styles.linkButtonMobile}`}
                      type="button"
                      onClick={() =>
                        copyLink({ vertical: "top", horizontal: "center" })
                      }
                    >
                      <img src={copyIcon} alt="複製" />
                      複製連結
                    </button>
                  </div>
                  {/* <div className={styles.qrText}>
                <span>結束時間：</span>
                <span>2024/06/XX 11:XX</span>
              </div> */}
                </div>
              </div>
              <BaseSnackbar
                anchorOrigin={{ vertical, horizontal }}
                open={open}
                handleClose={handleSnackbarClose}
                message={"連結已複製"}
              />
            </>
          )}
        </>
      );
    }
    case StepStatus.UnLogin:
      return <div className={styles.emptyContent}>請登入以使用功能</div>;
    case StepStatus.EndClose:
    default:
      return null;
  }
};

const AiSpeakBottom = ({
  stepStatus,
  onBack,
  qrCodeValue,
  annotationId,
  paperId,
  createSpeak,
  hasRecord
}) => {
  const resultSrc = `${naniSpeakUrl}/record?qrcode=${qrCodeValue}&annotationId=${annotationId}&paperId=${paperId}`;
  
  switch (stepStatus) {
    case StepStatus.Preparing:
    case StepStatus.Testing:
    case StepStatus.UnLogin: {
      return <></>;
    }
    case StepStatus.Ready: {
      return <div className={styles.actionsButton}>
        <button className={styles.primaryButton} onClick={() => { 
          createSpeak() 
          onBack() 
        }}>開始派題</button>
      {window.electron ? (
        <button
          className={classNames(styles.recordButton, { [styles.isDisable]: !hasRecord })}
          onClick={() => {
            window.electron &&
              window.electron.ipcRenderer.send("openExternal", resultSrc);
          }}
        >
          答題記錄
        </button>
      ) : (
        <a className={classNames(styles.recordButton, { [styles.isDisable]: !hasRecord })} href={resultSrc} target="_blank" rel="noopener noreferrer">
          <span>答題<br />記錄</span>
        </a>
      )}
    </div>
    }
    case StepStatus.EndClose:
    default:
      return null;
  }
};

const AiSpeak = () => {
  const [{ annotationId }] = useStore(StoreTypes.annotation);
  const [{ token, userId }] = useStore(StoreTypes.user);
  const [{ bookId }] = useStore(StoreTypes.books);
  const [{ aiSpeakSrc }, readerDispatch] = useStore(StoreTypes.reader);
  const [paperData, setPaperData] = useState({
    paperId: "",
    displayName: "",
    sentences: []
  });
  const [qrCodeValue] = useState(uuid());
  const [stepStatus, setStepStatus] = useState(StepStatus.Preparing);
  const [isLoading, setIsLoading] = useState(false);
  const [hasRecord, setHasRecord] = useState(false);
  const [, globalDispatch] = useStore(StoreTypes.global);
  const title = {
    [StepStatus.Preparing]: "題目準備中",
    [StepStatus.Ready]: "AI 練口說",
    [StepStatus.Testing]: "請掃描QR code或複製連結加入",
    [StepStatus.EndClose]: "確定要關閉嗎？",
    [StepStatus.UnLogin]: "請登入",
  };

  useEffect(() => {
    if (aiSpeakSrc === "unLogin") {
      setStepStatus(StepStatus.UnLogin);
      readerDispatch({ type: types.SET_AISPEAK_SRC, aiSpeakSrc: "" });
      return;
    }
    if (!aiSpeakSrc) return;

    const getMappingApi = async () => {
      setIsLoading(true);
      try {
        const getOne = await fetch(
          `${process.env.REACT_APP_NANISPEAK_API}/v1/papers/get/one`,
          {
            method: "POST",
            headers: {
              Authorization: token,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ paperCode: aiSpeakSrc }),
          }
        );
        const { data, err } = await getOne.json();

        if (err !== 0 || data === null) return;

        setPaperData({
          paperId: data.paperId,
          displayName: data.displayName,
          sentences: data.sentences,
        });
        setStepStatus(StepStatus.Ready);
      } catch (err) {
        console.log("AiSpeak getMappingApi error", err);
      } finally {
        setIsLoading(false);
      }
    };
    getMappingApi();

    return () => {
      readerDispatch({ type: types.SET_AISPEAK_SRC, aiSpeakSrc: "" });
    };
  }, [aiSpeakSrc, readerDispatch, token]);

  useEffect(() => {
    const checkRecord = async() => {
      if (!paperData.paperId || !annotationId) return;
      setIsLoading(true);
      try {
        const getRecord = await fetch(
          `${process.env.REACT_APP_NANISPEAK_API}/v1/exams/?paperId=${paperData.paperId}&annotationId=${annotationId}&page=1&limit=10&sort=-createdAt`,
          {
            method: "GET",
            headers: {
              Authorization: token,
              "Content-Type": "application/json",
            },
          }
        );
        const { data: { totalAmount } } = await getRecord.json();
        if (totalAmount > 0) {
          setHasRecord(true);
        }
      } catch (err) {
        console.log("AiSpeak getRecord error", err);
      } finally {
        setIsLoading(false);
      }
    };
    checkRecord();
  }, [paperData.paperId, annotationId])

  const createSpeak = () => {
    fetch(
      `${process.env.REACT_APP_NANISPEAK_API}/v1/exams`,
      {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          paperId: paperData.paperId,
          QRCode: qrCodeValue,
          annotationId,
          bookId,
          teacherId: userId,
          examType: "speak"
        }),
      }
    );
  }

  const handleClose = () => {
    globalDispatch({ type: types.CLOSE_AISPEAK_MODAL });
  };

  const handleGoBack = () => {
    setStepStatus(StepStatus.Testing);
  };

  const handleShowCloseButton = () => {
    return stepStatus !== StepStatus.EndClose;
  };

  return (
    <DragModal
      title={title[stepStatus]}
      showCloseButton={handleShowCloseButton()}
      onCloseButtonClick={handleClose}
      content={
        <AiSpeakContent
          stepStatus={stepStatus}
          isLoading={isLoading}
          paperData={paperData}
          qrCodeValue={qrCodeValue}
        />
      }
      bottom={
        <AiSpeakBottom
          stepStatus={stepStatus}
          isLoading={isLoading}
          onClose={handleClose}
          onBack={handleGoBack}
          qrCodeValue={qrCodeValue}
          annotationId={annotationId}
          paperId={paperData.paperId}
          createSpeak={createSpeak}
          hasRecord={hasRecord}
        />
      }
    />
  );
};

export default AiSpeak;
